import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "hooks/auth/useAuth";
import { setLastActivityTimestamp } from "@/core/activity/lastActivity";
import { useTimesheet } from "./useTimesheet";

const ACTIVITY_THROTTLE_MS = 2000; // update lastActivity at most every 2s

/**
 * Tracks user activity (mouse, keyboard, touch, scroll) while clocked in and updates
 * the shared lastActivity timestamp. That value is sent with WebSocket pings so the
 * server can persist it; the scheduled function (autoClockOutInactiveUsers) is the
 * single source of truth for actually clocking users out after 5 PM when inactive 15+ min.
 */
export function useInactivityClockOut() {
  const { userInfo } = useAuth();
  const { isTimerActive } = useTimesheet();
  const throttleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOwner = userInfo?.isOwner ?? false;

  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    if (throttleTimeoutRef.current !== null) return;
    setLastActivityTimestamp(now);
    throttleTimeoutRef.current = setTimeout(() => {
      throttleTimeoutRef.current = null;
    }, ACTIVITY_THROTTLE_MS);
  }, []);

  useEffect(() => {
    if (isOwner || !isTimerActive) return;

    const now = Date.now();
    setLastActivityTimestamp(now);

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"] as const;

    const handleActivity = () => {
      updateLastActivity();
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = null;
      }
    };
  }, [isOwner, isTimerActive, updateLastActivity]);
}
